/**
 * Envanter API servisi
 */

// API base: take env and ensure /inventory suffix
const API_URL_BASE = process.env.REACT_APP_API_URL || 'https://iyiliginmucitleri.com/api';
const API_URL = `${API_URL_BASE.replace(/\/$/, '')}/inventory`;

class InventoryService {
  /**
   * Authorization header oluştur
   */
  getAuthHeader() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // ============ Envanter Öğeleri ============

  /**
   * Tüm envanter öğelerini getir
   */
  async getAllItems(filters = {}) {
    const queryParams = new URLSearchParams();
    
    if (filters.workshop) queryParams.append('workshop', filters.workshop);
    if (filters.category) queryParams.append('category', filters.category);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.location) queryParams.append('location', filters.location);
    if (filters.search) queryParams.append('search', filters.search);

    const url = `${API_URL}/items${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeader()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Öğeler alınamadı');
    }

    return await response.json();
  }

  /**
   * Belirli bir envanter öğesini getir
   */
  async getItem(itemId) {
    const response = await fetch(`${API_URL}/items/${itemId}`, {
      method: 'GET',
      headers: this.getAuthHeader()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Öğe bulunamadı');
    }

    return await response.json();
  }

  /**
   * Yeni envanter öğesi oluştur
   */
  async createItem(itemData) {
    const response = await fetch(`${API_URL}/items`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: JSON.stringify(itemData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Öğe oluşturulamadı');
    }

    return await response.json();
  }

  /**
   * Envanter öğesini güncelle
   */
  async updateItem(itemId, itemData) {
    const response = await fetch(`${API_URL}/items/${itemId}`, {
      method: 'PUT',
      headers: this.getAuthHeader(),
      body: JSON.stringify(itemData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Öğe güncellenemedi');
    }

    return await response.json();
  }

  /**
   * Envanter öğesini sil
   */
  async deleteItem(itemId) {
    const response = await fetch(`${API_URL}/items/${itemId}`, {
      method: 'DELETE',
      headers: this.getAuthHeader()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Öğe silinemedi');
    }

    return await response.json();
  }

  /**
   * Konum seçenekleri (dolap / masa / çekmece)
   */
  async getLocations() {
    const response = await fetch(`${API_URL}/locations`, {
      method: 'GET',
      headers: this.getAuthHeader()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Konumlar alınamadı');
    }

    return await response.json();
  }

  /**
   * XML dosyasından toplu öğe aktarımı
   */
  async importItemsXml(file, workshop) {
    const formData = new FormData();
    formData.append('file', file);
    if (workshop) formData.append('workshop', workshop);

    const token = localStorage.getItem('token');

    const response = await fetch(`${API_URL}/items/import`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'XML aktarımı başarısız');
    }

    return await response.json();
  }

  // ============ Ödünç Talepleri (kullanıcı) ============

  /**
   * Talep edilebilir malzemeler
   */
  async getCatalog(filters = {}) {
    const queryParams = new URLSearchParams();

    if (filters.category) queryParams.append('category', filters.category);
    if (filters.location) queryParams.append('location', filters.location);
    if (filters.search) queryParams.append('search', filters.search);

    const url = `${API_URL}/catalog${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeader()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Malzemeler alınamadı');
    }

    return await response.json();
  }

  /**
   * Ödünç talebi oluştur (yönetici onayına düşer)
   */
  async createBorrowRequest(requestData) {
    const response = await fetch(`${API_URL}/borrow-requests`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Talep oluşturulamadı');
    }

    return await response.json();
  }

  /**
   * Kullanıcının kendi talepleri
   */
  async getMyBorrows() {
    const response = await fetch(`${API_URL}/my-borrows`, {
      method: 'GET',
      headers: this.getAuthHeader()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Talepler alınamadı');
    }

    return await response.json();
  }

  /**
   * Yönetici: talebi onayla
   */
  async approveBorrow(borrowId) {
    const response = await fetch(`${API_URL}/borrows/${borrowId}/approve`, {
      method: 'PUT',
      headers: this.getAuthHeader()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Talep onaylanamadı');
    }

    return await response.json();
  }

  /**
   * Yönetici: talebi reddet
   */
  async rejectBorrow(borrowId, reason) {
    const response = await fetch(`${API_URL}/borrows/${borrowId}/reject`, {
      method: 'PUT',
      headers: this.getAuthHeader(),
      body: JSON.stringify({ reason })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Talep reddedilemedi');
    }

    return await response.json();
  }

  // ============ Ödünç İşlemleri ============

  /**
   * Tüm ödünç kayıtlarını getir
   */
  async getAllBorrows(filters = {}) {
    const queryParams = new URLSearchParams();
    
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.itemId) queryParams.append('itemId', filters.itemId);
    if (filters.borrowerEmail) queryParams.append('borrowerEmail', filters.borrowerEmail);

    const url = `${API_URL}/borrows${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeader()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ödünç kayıtları alınamadı');
    }

    return await response.json();
  }

  /**
   * Yeni ödünç kaydı oluştur
   */
  async createBorrow(borrowData) {
    const response = await fetch(`${API_URL}/borrows`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: JSON.stringify(borrowData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ödünç kaydı oluşturulamadı');
    }

    return await response.json();
  }

  /**
   * Ödünç alınan öğeyi iade et
   */
  async returnItem(borrowId, returnData) {
    const response = await fetch(`${API_URL}/borrows/${borrowId}/return`, {
      method: 'PUT',
      headers: this.getAuthHeader(),
      body: JSON.stringify(returnData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'İade işlemi başarısız');
    }

    return await response.json();
  }

  // ============ Aylık Kontroller ============

  /**
   * Aylık kontrol kayıtlarını getir
   */
  async getMonthlyChecks(workshop = null, limit = 10) {
    const queryParams = new URLSearchParams();
    
    if (workshop) queryParams.append('workshop', workshop);
    queryParams.append('limit', limit);

    const url = `${API_URL}/monthly-checks?${queryParams.toString()}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeader()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Kontrol kayıtları alınamadı');
    }

    return await response.json();
  }

  /**
   * Yeni aylık kontrol kaydı oluştur
   */
  async createMonthlyCheck(checkData) {
    const response = await fetch(`${API_URL}/monthly-checks`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: JSON.stringify(checkData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Kontrol kaydı oluşturulamadı');
    }

    return await response.json();
  }

  // ============ İstatistikler ============

  /**
   * Envanter istatistiklerini getir
   */
  async getStatistics(workshop = null) {
    const queryParams = new URLSearchParams();
    
    if (workshop) queryParams.append('workshop', workshop);

    const url = `${API_URL}/statistics${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeader()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'İstatistikler alınamadı');
    }

    return await response.json();
  }

  // ============ Fotoğraf Yükleme ============

  /**
   * Fotoğraf yükle
   */
  async uploadPhoto(file) {
    const formData = new FormData();
    formData.append('photo', file);

    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_URL}/upload-photo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Fotoğraf yüklenemedi');
    }

    return await response.json();
  }
}

export default new InventoryService();
